import React, { useMemo } from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getAlertTypes, saveAlertTypesBatch } from '../../api/alertTypes';
import type { AlertType } from '../../types/AlertType';
import { t } from '../../i18n/t';

// SCOPE_OPTIONS / SEVERITY_OPTIONS — small fixed lookups, no API call. The
// underlying DB has CHECK constraints for both columns; the dropdowns are the
// UI mirror so a user can't type a value that won't pass server validation.
const SCOPE_OPTIONS: { value: 'tenant' | 'global'; label: string }[] = [
  { value: 'tenant', label: 'tenant' },
  { value: 'global', label: 'global' },
];

const SEVERITY_OPTIONS: { value: 1 | 2 | 3; label: string }[] = [
  { value: 1, label: t('alertTypes.severity.critical') },
  { value: 2, label: t('alertTypes.severity.severe') },
  { value: 3, label: t('alertTypes.severity.light') },
];

const newAlertTypeDefaults = (): Partial<AlertType> => ({
  alertTypeId: 0,
  alertTypeCode: '',
  alertTypeName: '',
  scope: 'tenant',
  severity: 2,
});

export function AlertTypesPage() {
  const columnDefs = useMemo<ColDef<AlertType>[]>(() => {
    // Severity label maps (cellDataType:'text' dropdown pattern — see CLAUDE.md).
    const idToLabel = new Map<number, string>(
      SEVERITY_OPTIONS.map((s) => [s.value, s.label]),
    );
    const labelToId = new Map<string, number>(
      SEVERITY_OPTIONS.map((s) => [s.label, s.value]),
    );

    return [
      {
        field: 'alertTypeId',
        headerName: t('alertTypes.columns.alertTypeId'),
        width: 100,
        editable: (params: EditableCallbackParams<AlertType>) =>
          (params.data as { __status?: string } | undefined)?.__status === 'added',
        cellDataType: 'number',
        sort: 'asc',
      },
      {
        field: 'alertTypeCode',
        headerName: t('alertTypes.columns.alertTypeCode'),
        width: 200,
        editable: true,
      },
      {
        field: 'alertTypeName',
        headerName: t('alertTypes.columns.alertTypeName'),
        flex: 1,
        editable: true,
      },
      {
        field: 'scope',
        headerName: t('alertTypes.columns.scope'),
        width: 120,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: SCOPE_OPTIONS.map((s) => s.value) },
      },
      {
        field: 'severity',
        headerName: t('alertTypes.columns.severity'),
        width: 130,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: SEVERITY_OPTIONS.map((s) => s.label) },
        valueGetter: (p: ValueGetterParams<AlertType>) => {
          const v = p.data?.severity;
          if (v === undefined || v === null) return '';
          return idToLabel.get(v) ?? String(v);
        },
        valueSetter: (p: ValueSetterParams<AlertType>) => {
          const id = labelToId.get(String(p.newValue));
          if (id === undefined) return false;
          p.data.severity = id as 1 | 2 | 3;
          return true;
        },
      },
    ];
  }, []);

  return (
    <LopCrudPage<AlertType>
      resourceCode="alertTypes"
      titleKey="alertTypes.pageTitle"
      idField="alertTypeId"
      getRows={getAlertTypes}
      saveBatch={(added, modified, deletedIds) =>
        saveAlertTypesBatch(added, modified, deletedIds as number[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newAlertTypeDefaults}
    />
  );
}
